import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import Table from 'rc-table';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { createStructuredSelector } from 'reselect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { selectCompanyInvoices, selectVehicleInvoices } from '../selectors';
import { BaseComponent } from '../../../components/BaseComponent';
import { formatCurrency, formatLicensePlate } from '../../../utils/string';
import { InvoiceListItem } from './InvoiceListItem';
import styles from './InvoicesCompanyTable.module.css';
import { selectWebloginVehicleInvoices } from '../../Weblogin/selectors';

const Container = styled.div`
  width: 100%;
  background: white;
`;

const CustomTable = styled(Table)`
  width: 100%;
  display: flex;
  font-size: 12px;

  .rc-table-container {
    width: 100%;
  }

  .rc-table-content table {
    width: 100%;
  }

  .rc-table-thead {
    background-color: var(--color-new-light);
    font-weight: 600;
    height: 52px;
    padding: 14px 0;
    margin-left: 6px;
    font-size: 14px;
    white-space: nowrap;
  }

  td,
  th {
    padding: 8px 12px;
  }
`;

const DescriptionCell = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 200px;
  justify-content: center;
`;

const PaidWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const IconWrapper = styled.div`
  color: ${props => (props.color === 'green' ? '#11ab79' : props.color === 'orange' ? '#FC9629' : '#d50046')};
`;

export const ActionsContainer = styled.div`
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const VEHICLE_INCOMING_TYPE = 'vehicle incoming';
const PRODUCT_INVOICE_TYPE = 'invoice.subject.type.product';
const PRODUCT_INVOICE_TYPE_OUTGOING = 'Product outgoing';
/* eslint-disable react/prefer-stateless-function */
class InvoicesCompanyTableComp extends BaseComponent {
  constructor(props) {
    super(props);
  }

  get invoices() {
    if (this.props.weblogin) {
      return this.props.webloginVehicleInvoices;
    }
    if (this.props.companyDetails) {
      return this.props.companyInvoices;
    }
    if (this.props.vehicleDetails) {
      return this.props.vehicleInvoices;
    }
  }

  isVehicleTranslatedTypeByDirection(item, direction, type) {
    const localSubject = this.lblStr(`invoice.subject.type.${type}`);
    const translatedType = `${localSubject} ${direction}`;
    return item?.type?.toLowerCase()?.includes(translatedType?.toLowerCase());
  }

  formatDate = dateString => {
    if (!dateString) return '-';
    return dayjs(dateString).format('DD-MM-YYYY');
  };

  getData = invoice => {
    const localSubject = this.lblStr(`invoice.subject.type.${invoice?.subjectType}`);
    const type = `${localSubject} ${invoice?.direction}`;
    const otherCompany = invoice?.direction === 'outgoing' ? invoice.outgoingCompany : invoice.incomingCompany;

    const vehicle = invoice?.vehicle
      ? invoice?.vehicle?.properties?.reduce(
          (acc, nxt) => ({ ...acc, [nxt?.propertyName?.name]: nxt?.propertyValue?.value }),
          {},
        )
      : null;
    const statusColors = {
      PAID: 'green',
      PARTLY_PAID: 'orange',
      DECLINED: 'red',
      OPEN: 'red',
    };

    return {
      type,
      credit: this.lblStr(`invoice.type.${invoice.type}`),
      description: !vehicle
        ? { description: invoice?.description }
        : {
            name: `${vehicle?.brand_name} ${vehicle?.model_name}`,
            info: vehicle?.model_info ? ` | ${vehicle?.model_info}` : '-',
            regplate: vehicle?.regplate ? ` | ${formatLicensePlate(vehicle?.regplate)}` : '',
            vin: vehicle?.vin ? ` | ${vehicle?.vin}` : '',
            vehicle: invoice?.vehicle,
          },
      invoiceNumber: {
        value: invoice?.entryNumber ?? '-',
        date: this.formatDate(invoice?.date),
        isIncoming: invoice?.direction === 'incoming',
      },
      total: {
        amount: invoice?.transactionAmountDc
          ? formatCurrency(invoice?.transactionAmountDc)
          : formatCurrency(invoice?.financialTotalAmount),
        isPaid: invoice?.status?.name === 'PAID' || invoice?.status?.name === 'PARTLY_PAID',
        color: statusColors[invoice?.status?.name],
        date: invoice?.paymentDate
          ? this.formatDate(invoice?.paymentDate)
          : this.lbl(`invoice.paid.${invoice?.status?.name}`),
        paymentAmount: invoice?.paymentAmount ? formatCurrency(invoice?.paymentAmount) : formatCurrency('0.00'),
      },
      id: invoice?.id,
      actions: this.props.weblogin ? {} : invoice,
    };
  };

  getColumns = () => {
    if (this.props.columns?.length) {
      return this.props.columns;
    }

    return [
      { title: this.lblStr('invoice.column.type'), dataIndex: 'type', key: 'type', align: 'left' },
      {
        title: this.lblStr('invoice.column.model'),
        dataIndex: 'description',
        key: 'description',
        align: 'left',
        render(value, row, index) {
          return (
            <DescriptionCell>
              {value?.description ? (
                <span style={{ marginBottom: 5 }}>{value.description}</span>
              ) : (
                <span style={{ marginBottom: 5 }}>
                  <strong>{value?.name}</strong>
                  {value?.info}
                  {value.vin}
                  {formatLicensePlate(value.regplate)}
                </span>
              )}
            </DescriptionCell>
          );
        },
      },

      {
        title: this.lblStr('invoice.column.invoice'),
        dataIndex: 'invoiceNumber',
        key: 'invoiceNumber',
        align: 'left',
        render(value, row, index) {
          return (
            <DescriptionCell>
              <PaidWrapper>
                <IconWrapper color={value?.isIncoming ? 'green' : ''}>
                  <FontAwesomeIcon icon={value?.isIncoming ? faArrowDown : faArrowUp} />
                </IconWrapper>
                <strong style={{ whiteSpace: 'nowrap' }}>{value?.value}</strong>
              </PaidWrapper>
              <span style={{ whiteSpace: 'nowrap' }}>{value?.date}</span>
            </DescriptionCell>
          );
        },
      },
      {
        title: this.lblStr('invoice.column.paid'),
        dataIndex: 'total',
        key: 'total',
        align: 'left',
        render(value) {
          return (
            <PaidWrapper>
              <IconWrapper color={value?.color}>
                <FontAwesomeIcon icon={value?.isPaid ? faCheckCircle : faTimesCircle} />
              </IconWrapper>
              <DescriptionCell>
                <strong>{`${value.paymentAmount} / ${value?.amount}`}</strong>
                <span>{value?.date ?? ''}</span>
              </DescriptionCell>
            </PaidWrapper>
          );
        },
      },
      { title: 'Credit', dataIndex: 'credit', key: 'credit', align: 'left' },

      {
        key: 'actions',
        dataIndex: 'actions',
        render: value => {
          const ActionsColComp = this.props.ActionsColComp;
          if (ActionsColComp) {
            return <ActionsColComp value={value} />;
          }

          return (
            value?.id && (
              <ActionsContainer>
                <IconWrapper color={'green'}>
                  <InvoiceListItem
                    className={styles.iconsContainer}
                    invoice={value}
                    userCreatorId={value?.userCreatorId}
                    financialContactId={value?.financialContactId}
                    outgoingUserId={value?.outgoingUserId}
                    emailContactId={value?.emailContactId}
                    key={value?.id}
                  />
                </IconWrapper>
              </ActionsContainer>
            )
          );
        },
      },
    ];
  };

  to(url, newWindow) {
    //
    if (newWindow) {
      window.open(url);
    } else {
      this.props.history.push(url);
    }
  }

  tofn(url) {
    return event => {
      this.to(url, event?.ctrlKey || event?.metaKey);
    };
  }

  render() {
    const columns = this.getColumns();
    const data = this.invoices?.map(invoice => this.getData(invoice));
    this.formatDate(data[0]?.date);

    return (
      <Container>
        <CustomTable
          columns={columns}
          data={data}
          scroll={{ x: window.innerWidth < 975 ? 975 : 975 }}
          emptyText={this.lbl('vehicles.overview.column.no_invoice_found')}
          onRow={(record, index) => ({
            onClick: event => {
              if (this.props.weblogin) return;
              if (this.isVehicleTranslatedTypeByDirection(record, 'incoming', 'vehicle')) {
                return this.to(`/invoices/incoming/${record.id}`, event?.ctrlKey || event?.metaKey);
              }

              if (this.isVehicleTranslatedTypeByDirection(record, 'outgoing', 'vehicle')) {
                return this.to(`/invoices/outgoing/${record.id}`, event?.ctrlKey || event?.metaKey);
              }
              if (this.isVehicleTranslatedTypeByDirection(record, 'outgoing', 'BPM')) {
                return this.to(`/invoices/outgoing/${record.id}`, event?.ctrlKey || event?.metaKey);
              }

              if (this.isVehicleTranslatedTypeByDirection(record, 'outgoing', 'product')) {
                let vehicleId = this.props.vehicleId || record?.actions?.vehicleId;
                if (vehicleId) {
                  return this.to(
                    `/invoices/product-invoices/vehicle/${vehicleId}/invoice/${record.id}`,
                    event?.ctrlKey || event?.metaKey,
                  );
                }

                return this.to(`/invoices/product-invoices/invoice/${record?.id}`, event?.ctrlKey || event?.metaKey);
              }

              return this.to(`/invoices/outgoing/${record.id}`, event?.ctrlKey || event?.metaKey);
            },

            invoice: this.invoices[index],
          })}
        />
      </Container>
    );
  }
}

InvoicesCompanyTableComp.propTypes = {
  dispatch: PropTypes.func.isRequired,
  onItemClick: PropTypes.func,
};

const mapStateToProps = (state, ownProps) =>
  createStructuredSelector({
    companyInvoices: selectCompanyInvoices(),
    vehicleInvoices: selectVehicleInvoices(),
    webloginVehicleInvoices: selectWebloginVehicleInvoices(),
  });

function mapDispatchToProps(dispatch, ownProps) {
  return {
    dispatch,
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export const InvoicesCompanyTable = compose(withConnect, withRouter, injectIntl)(InvoicesCompanyTableComp);
